import classNames from 'classnames';
import React, {useMemo} from 'react';

import type {DiscoveryUnit} from '@udemy/discovery-api';
import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {useI18n} from '@udemy/i18n';
import {
    type CertificationBundle,
    CertificationVoucherBundleUnitCarousel,
    PricingOption,
    trackVoucherOfferPresented,
    type VoucherUIRegion,
} from '@udemy/react-certification-preparation';
import {
    selectPools,
    useExamAvailability,
    useVoucherPricing,
} from '@udemy/react-certification-vouchers';
import {ExperimentImpressionEvent, useFeatures} from '@udemy/react-experimentation-provider';

import {
    CertificationVoucherProviderType,
    CourseSubscriptionAvailabilityStatus,
    GetCertificationVoucherExamBySlugQuery,
    useCoursesSubscriptionAvailabilityQuery,
    VoucherPoolProductType,
} from 'src/generated/graphql';
import {useIsMobileAppView} from 'src/hooks/useIsMobileAppView';
import {SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT} from 'src/lib/experimentation-constants';

import {SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS} from '../topic/constants';
import {Banner, VoucherBulletPointIcon} from './components/banner/banner.react-component';
import {CoursesToGetYouStarted} from './components/courses-to-get-you-started/courses-to-get-you-started.react-component';
import {Description} from './components/description/description.react-component';
import {trackViewLandingPage} from './events';
import {usePoolIdsForEvent} from './hooks/use-pool-ids-for-event';
import styles from './voucher-landing-page.module.less';

export type VoucherData = {
    exam: NonNullable<GetCertificationVoucherExamBySlugQuery['certificationVoucherExamBySlug']>;
    /** "Courses to get you started" unit */
    coursesToGetYouStartedUnit?: DiscoveryUnit | null;
    certificationBundles: CertificationBundle[];
};

export const VoucherLandingPage = ({voucherData}: {voucherData: VoucherData}) => {
    const {exam, coursesToGetYouStartedUnit, certificationBundles} = voucherData;
    const {ninterpolate, gettext} = useI18n();
    const {isReady, isMobileApp} = useIsMobileAppView();
    // Fail-safe: until we know for sure this isn't the native app's webview, treat it as if it
    // were, so restricted content never renders and then disappears once we find out.
    const hideForMobileApp = !isReady || isMobileApp;

    const hasBundle = useMemo(() => {
        return certificationBundles.length > 0;
    }, [certificationBundles.length]);

    const bundleCourseIds = certificationBundles.flatMap((bundle) =>
        bundle.contents.map((course) => String(course.id)),
    );
    const {data: bundleSubscriptionData, isLoading: isBundleSubscriptionLoading} =
        useCoursesSubscriptionAvailabilityQuery(
            {ids: bundleCourseIds},
            {enabled: bundleCourseIds.length > 0},
        );
    const allCoursesExcludedFromSubscription =
        !isBundleSubscriptionLoading &&
        (bundleSubscriptionData?.courses ?? []).every(
            (course) =>
                course?.subscriptionInfo?.availabilityStatus ===
                CourseSubscriptionAvailabilityStatus.None,
        );

    const {isLoading: isFeaturesLoading, featureVariantAssignments} = useFeatures([
        SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT,
    ]);

    const certificationVoucherBundleUnitFeature = featureVariantAssignments.get(
        SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT,
    );
    const showCertificationVoucherBundleUnit =
        certificationVoucherBundleUnitFeature?.configuration?.showCertificationVoucherBundleUnit ??
        false;

    const voucherAvailability = useExamAvailability(exam.id);
    const {primaryPool, retakeInsurancePool, defaultPool, hasBothPoolsAvailable} =
        selectPools(exam);

    const isUdemyIssuedRetake =
        primaryPool?.productType === VoucherPoolProductType.UdemyIssuedRetakeInsurance;

    const {
        isLoading: isVoucherPoolPricesLoading,
        singlePoolPrice,
        retakeInsurancePrice,
        defaultPrice,
        hasDiscount,
        discountPercentage,
        hasStock: pricingHasStock,
        effectiveHasBothPoolsAvailable,
    } = useVoucherPricing({
        voucherPool: primaryPool,
        retakeInsurancePool,
        defaultPool,
        hasBothPoolsAvailable,
        isExamLoading: voucherAvailability === undefined,
    });

    // Both SKUs exist but only one has stock: keep showing both so the learner can tell
    // which voucher they are buying, with the sold-out one marked as unavailable.
    const unavailableOption = useMemo(() => {
        if (effectiveHasBothPoolsAvailable || !retakeInsurancePool || !defaultPool) {
            return undefined;
        }
        if (retakeInsurancePool.hasAvailableVouchers === false) {
            return PricingOption.Retake;
        }
        if (defaultPool.hasAvailableVouchers === false) {
            return PricingOption.Base;
        }
        return undefined;
    }, [effectiveHasBothPoolsAvailable, retakeInsurancePool, defaultPool]);

    // Preserve undefined while exam availability is loading so Banner renders a skeleton
    const hasStock: boolean | undefined =
        voucherAvailability === undefined || isVoucherPoolPricesLoading
            ? undefined
            : pricingHasStock;

    const providerType = exam.providerDetails.type;

    const validityBullets = (() => {
        // Linux Foundation authors its own validity terms, which differ from every other provider.
        // TODO: CER-437: Delete this as part of the cleanup where the bullet points will stored in the backend.
        if (providerType === CertificationVoucherProviderType.LinuxFoundation) {
            return [];
        }

        if (isUdemyIssuedRetake && providerType === CertificationVoucherProviderType.Aws) {
            return [
                gettext('Voucher valid for at least 9 months from the purchase date.'),
                gettext(
                    'Second Chance (if purchased and issued) valid for at least 9 months from the issuance date.',
                ),
            ];
        }

        // The validity window is per-pool data from the API, not a per-provider constant, so
        // CompTIA's longer window arrives on its own and no provider needs special-casing here.
        const expiredMonths = primaryPool?.expirationThresholdInMonths ?? 12;
        if (
            effectiveHasBothPoolsAvailable &&
            providerType !== CertificationVoucherProviderType.Aws
        ) {
            return [
                ninterpolate(
                    'Voucher and Retake (if purchased) valid for at least %(months)s month from the purchase date.',
                    'Voucher and Retake (if purchased) valid for at least %(months)s months from the purchase date.',
                    expiredMonths,
                    {months: expiredMonths},
                ),
            ];
        }

        if (!isUdemyIssuedRetake && !effectiveHasBothPoolsAvailable) {
            return [
                ninterpolate(
                    'Voucher valid for at least %(months)s month from the purchase date.',
                    'Voucher valid for at least %(months)s months from the purchase date.',
                    expiredMonths,
                    {months: expiredMonths},
                ),
            ];
        }

        return [];
    })();

    const poolIdsForEvent = usePoolIdsForEvent({
        exam,
        voucherAvailability,
        retakeInsurancePrice,
        defaultPrice,
        isVoucherPoolPricesLoading,
    });

    const trackFunc = React.useCallback(() => {
        trackViewLandingPage('425.00', '382.50', '382.50');
        trackVoucherOfferPresented(
            {
                name: exam.title,
                examId: exam.id,
                poolId: '',
                voucherId: null,
            },
            poolIdsForEvent.length > 0 ? poolIdsForEvent : [exam.id],
            'voucher_landing' as VoucherUIRegion,
        );
    }, [exam.title, exam.id, poolIdsForEvent]);

    const content = (
        <section aria-labelledby="voucher-landing-page-heading">
            <Banner
                voucherId={exam.id}
                isUdemyIssuedRetake={isUdemyIssuedRetake}
                retakePatternImageUrl={exam.retakePatternImageUrl}
                logoUrl={exam.imageUrl ?? undefined}
                content={{
                    title: exam.title,
                    description: exam.description ?? '',
                    bulletPoints: [
                        ...validityBullets.map((bulletPoint) => ({
                            icon: VoucherBulletPointIcon.Date,
                            text: bulletPoint,
                        })),
                        ...(exam.valuePropositions
                            ?.filter((proposition): proposition is string => proposition !== null)
                            ?.map((proposition) => ({
                                icon: VoucherBulletPointIcon.Doc,
                                text: proposition,
                            })) ?? []),
                    ],
                }}
                pricing={{
                    singlePoolPrice,
                    retakeInsurancePrice,
                    defaultPrice,
                    isLoading: isVoucherPoolPricesLoading,
                }}
                availability={{
                    hasStock,
                    isAvailableForRegion: voucherAvailability?.isAvailableForRegion,
                    hasBothPoolsAvailable: effectiveHasBothPoolsAvailable,
                    unavailableOption,
                }}
                poolIds={{
                    poolId: primaryPool?.id ?? '',
                    retakeInsurancePoolId: retakeInsurancePool?.id,
                    defaultPoolId: defaultPool?.id,
                }}
                ctaMode={hideForMobileApp ? 'buyNow' : 'both'}
            />

            <Description
                questionAnswers={exam.questionsAndAnswers.map((qa) => ({
                    question: qa.question,
                    answerHTML: qa.answer,
                }))}
                discountPercentage={discountPercentage.toString()}
                hasDiscount={hasDiscount}
                examTitle={exam.title}
                examId={exam.id}
                examProviderType={exam.providerDetails.type}
                poolId={primaryPool?.id ?? ''}
                isUdemyIssuedRetake={isUdemyIssuedRetake}
            />
            {!hideForMobileApp &&
                hasBundle &&
                !isFeaturesLoading &&
                allCoursesExcludedFromSubscription && (
                    <TrackImpression
                        trackFunc={() => {
                            if (certificationVoucherBundleUnitFeature) {
                                const {experimentIds = [], isLocalDefault = false} =
                                    certificationVoucherBundleUnitFeature;
                                Tracker.publishEvent(
                                    new ExperimentImpressionEvent(experimentIds, isLocalDefault),
                                );
                            }
                        }}
                    >
                        <div />
                    </TrackImpression>
                )}
            {!hideForMobileApp &&
                !isFeaturesLoading &&
                showCertificationVoucherBundleUnit &&
                hasBundle &&
                allCoursesExcludedFromSubscription && (
                    <div
                        className={classNames(
                            'ud-container',
                            styles['voucher-bundle-unit-container'],
                        )}
                    >
                        <FunnelLogContextProvider pageType="individualVoucher">
                            <CertificationVoucherBundleUnitCarousel
                                title={gettext('Get Certified with Top Instructors')}
                                subtitle={gettext(
                                    'Explore curated bundles from expert instructors. Browse to compare teaching styles and prep strategies.',
                                )}
                                units={certificationBundles.map((bundle) => ({
                                    bundle: bundle,
                                    showCommonInstructor: true,
                                    ...SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS,
                                }))}
                            />
                        </FunnelLogContextProvider>
                    </div>
                )}
            {!hideForMobileApp && (
                <CoursesToGetYouStarted
                    coursesToGetYouStartedUnit={coursesToGetYouStartedUnit}
                    redirectTopicUrl={exam?.topic?.url}
                />
            )}
        </section>
    );

    // Only wrap with TrackImpression after pricing has loaded
    // This ensures poolIdsForEvent has the correct value before the event is sent
    if (isVoucherPoolPricesLoading) {
        return content;
    }

    return (
        <TrackImpression trackFunc={trackFunc} visibilityThreshold={0.01}>
            {content}
        </TrackImpression>
    );
};
